<!-- src/app/pages/hours-tracker/hours-tracker.component.html -->
<div class="hours-tracker">
  <div class="container-fluid pt-4">
    <h1 class="page-title text-center">Hours Tracker</h1>

    <!-- Loading -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <ng-container *ngIf="!loading">

      <!-- Clock Section -->
      <div class="clock-section card mb-4" [class.clocked-in]="activeShift" [class.clocked-out]="!activeShift">
        <div class="card-body">

          <!-- Not logged in -->
          <ng-container *ngIf="!isAuthenticated">
            <div class="status-badge status-out">Currently clocked out</div>
            <a class="btn btn-primary btn-clock mt-3" routerLink="/login" [queryParams]="{ returnUrl: '/hours-tracker' }">Log In</a>
          </ng-container>

          <!-- Logged in, active shift -->
          <ng-container *ngIf="isAuthenticated && activeShift">
            <div class="clock-status-pill">
              <span class="clocked-in-text">
                Clocked in since {{ formatTime(activeShift.clockIn) }}
                <span *ngIf="elapsedTime" class="elapsed">· {{ elapsedTime }}</span>
              </span>
            </div>
            <button class="btn btn-danger btn-clock mt-3" (click)="clockOut()">Clock Out</button>
          </ng-container>

          <!-- Logged in, clocked out, pay period covers today -->
          <ng-container *ngIf="isAuthenticated && !activeShift && todayHasPeriod">
            <div class="status-badge status-out">Currently clocked out</div>
            <button class="btn btn-success btn-clock mt-3" (click)="clockIn()">Clock In</button>
          </ng-container>

          <!-- Logged in, clocked out, no period covers today -->
          <ng-container *ngIf="isAuthenticated && !activeShift && !todayHasPeriod">
            <div class="status-badge status-out">Currently clocked out</div>
            <p class="no-period-msg mt-2 mb-0">Create a pay period to start clocking in.</p>
          </ng-container>

        </div>
      </div>

      <!-- Pay Periods -->
      <div class="pay-periods">

        <div *ngIf="payPeriods.length === 0" class="empty-state">
          <p>No pay periods defined yet.</p>
        </div>

        <div class="period-card card mb-3" *ngFor="let period of payPeriods">

          <!-- Period header (clickable accordion toggle) -->
          <div class="period-header" (click)="togglePeriod(period)">
            <div class="period-header-inner">
              <span class="period-range">{{ period.label || formatPeriodRange(period) }}</span>
              <span class="hours-badge">{{ period.totalHours }} hrs</span>
            </div>
            <span class="chevron" [class.open]="period.isOpen">▾</span>
          </div>

          <!-- Period body (expanded) -->
          <div class="period-body" *ngIf="period.isOpen">
            <div class="table-responsive">
              <table class="table table-sm shift-table mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Clock In</th>
                    <th>Clock Out</th>
                    <th class="text-right">Hours</th>
                    <th *ngIf="isAuthenticated" class="edit-col"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let shift of period.shifts">
                    <td>{{ formatDate(shift.clockIn) }}</td>
                    <td>{{ formatTime(shift.clockIn) }}</td>
                    <td>{{ formatTime(shift.clockOut!) }}</td>
                    <td class="text-right">{{ calcShiftHours(shift) }}</td>
                    <td *ngIf="isAuthenticated">
                      <button class="btn btn-sm btn-link edit-btn" (click)="openEditShift(shift)">Edit</button>
                    </td>
                  </tr>
                  <tr *ngIf="period.shifts.length === 0">
                    <td [attr.colspan]="isAuthenticated ? 5 : 4" class="text-center text-muted py-3">
                      No shifts in this period.
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="total-row">
                    <td colspan="3" class="text-right font-weight-bold">Total</td>
                    <td class="text-right font-weight-bold">{{ period.totalHours }}</td>
                    <td *ngIf="isAuthenticated"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div *ngIf="isAuthenticated" class="add-shift-row">
              <button class="btn btn-sm btn-outline-primary" (click)="openAddShift(period)">+ Add Shift</button>
              <button class="btn btn-sm btn-link edit-period-btn" (click)="openEditPeriod(period)">Edit Period</button>
            </div>
          </div>

        </div>

        <div *ngIf="isAuthenticated" class="add-period-row mt-2">
          <button class="btn btn-outline-secondary btn-block" (click)="openAddPeriod()">+ New Pay Period</button>
        </div>

      </div>
    </ng-container>
  </div>

  <!-- Add / Edit Shift Modal -->
  <div class="modal-backdrop" *ngIf="showShiftModal && isAuthenticated" (click)="closeShiftModal()">
    <div class="modal-card" (click)="$event.stopPropagation()">
      <div class="modal-header-row">
        <h5>{{ editingShift ? 'Edit Shift' : 'Add Shift' }}</h5>
        <button class="btn-close-modal" (click)="closeShiftModal()">✕</button>
      </div>
      <div class="modal-body-inner">
        <div class="form-group">
          <label>Date</label>
          <input type="date" class="form-control" [(ngModel)]="shiftForm.date">
        </div>
        <div class="form-group">
          <label>Clock In</label>
          <input type="time" class="form-control" [(ngModel)]="shiftForm.clockIn">
        </div>
        <div class="form-group">
          <label>Clock Out</label>
          <input type="time" class="form-control" [(ngModel)]="shiftForm.clockOut">
        </div>
        <div *ngIf="shiftFormError" class="alert alert-danger mt-2">{{ shiftFormError }}</div>
      </div>
      <div class="modal-footer-row">
        <button *ngIf="editingShift" class="btn btn-danger mr-auto" (click)="deleteShift()">Delete</button>
        <button class="btn btn-secondary" (click)="closeShiftModal()">Cancel</button>
        <button class="btn btn-primary ml-2" (click)="saveShift()">Save</button>
      </div>
    </div>
  </div>

  <!-- Add Pay Period Modal -->
  <div class="modal-backdrop" *ngIf="showPeriodModal && isAuthenticated" (click)="closePeriodModal()">
    <div class="modal-card" (click)="$event.stopPropagation()">
      <div class="modal-header-row">
        <h5>{{ editingPeriod ? 'Edit Pay Period' : 'New Pay Period' }}</h5>
        <button class="btn-close-modal" (click)="closePeriodModal()">✕</button>
      </div>
      <div class="modal-body-inner">
        <div class="form-group">
          <label>Start Date</label>
          <input type="date" class="form-control" [(ngModel)]="periodForm.startDate">
        </div>
        <div class="form-group">
          <label>End Date</label>
          <input type="date" class="form-control" [(ngModel)]="periodForm.endDate">
        </div>
        <div class="form-group">
          <label>Label <span class="text-muted">(optional)</span></label>
          <input type="text" class="form-control" [(ngModel)]="periodForm.label" placeholder="e.g. April Week 2">
        </div>
        <div *ngIf="periodFormError" class="alert alert-danger mt-2">{{ periodFormError }}</div>
      </div>
      <div class="modal-footer-row">
        <button *ngIf="editingPeriod" class="btn btn-danger mr-auto" (click)="deletePeriod()">Delete</button>
        <button class="btn btn-secondary" (click)="closePeriodModal()">Cancel</button>
        <button class="btn btn-primary ml-2" (click)="savePeriod()">Save</button>
      </div>
    </div>
  </div>

</div>
